<template>
  <v-container class="landing-container">
    <!-- Hero Section -->
    <v-row class="mb-16">
      <v-col cols="12" class="hero-section">
        <v-container>
          <v-row align="center" justify="center">
            <v-col cols="12" md="6" class="text-center">
              <h1 class="text-h2 font-weight-bold mb-6" style="color: #2ab3b8">
                Automate Your Workflows
              </h1>
              <p class="text-h5 mb-8">Simple visual boards that automate your business processes</p>
              <div class="d-flex justify-center">
                <v-btn x-large color="#2ab3b8" class="white--text mr-4" elevation="2" to="/login">
                  Get Started
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
                <v-btn x-large color="#2ab3b8" class="white--text mr-4" elevation="2" to="/login">
                  Log In
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center justify-center">
              <v-img
                :src="hero_url"
                alt="Process Automation Hero"
                max-width="500"
                class="rounded-lg"
              ></v-img>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <!-- Use Cases Section -->
    <v-row id="use-cases" class="mb-16">
      <v-col cols="12">
        <h2 class="text-h3 text-center mb-12" style="color: #2ab3b8">Use Cases</h2>
        <v-row>
          <v-col v-for="(useCase, index) in useCases" :key="index" cols="12" sm="6">
            <v-card elevation="2" hover style="border-radius: 12px" class="pa-6 h-100">
              <v-row no-gutters>
                <v-col>
                  <h3 class="text-h4 mb-4">{{ useCase.title }}</h3>
                  <p class="text-h6 grey--text text--darken-1">
                    {{ useCase.description }}
                  </p>
                </v-col>
                <v-col cols="auto">
                  <v-icon color="#2ab3b8" size="32">mdi-arrow-top-right</v-icon>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Metrics Section -->
    <v-row id="metrics" class="mb-16">
      <v-col cols="12">
        <v-card color="#2ab3b8" dark class="pa-12">
          <v-row>
            <v-col
              v-for="(metric, index) in metrics"
              :key="index"
              cols="12"
              md="4"
              class="text-center"
            >
              <v-icon size="48" class="mb-4">{{ metric.icon }}</v-icon>
              <h3 class="text-h2 font-weight-bold mb-4">{{ metric.stat }}</h3>
              <p class="text-h6">{{ metric.description }}</p>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Benefits Section -->
    <v-row id="benefits" class="mb-16">
      <v-col cols="12">
        <h2 class="text-h3 text-center mb-12" style="color: #2ab3b8">
          Why Choose DevCentre Process Automation?
        </h2>
        <v-row>
          <v-col v-for="(benefit, index) in benefits" :key="index" cols="12" md="3">
            <v-card flat style="border: 2px solid #2ab3b8; border-radius: 12px" class="pa-6 h-100">
              <v-icon color="#2ab3b8" size="36" class="mb-4">{{ benefit.icon }}</v-icon>
              <h3 class="text-h5 mb-4">{{ benefit.title }}</h3>
              <p class="text-subtitle-1">{{ benefit.description }}</p>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Features Section -->
    <v-row id="features" class="mb-16">
      <v-col cols="12">
        <h2 class="text-h3 text-center mb-12" style="color: #2ab3b8">Powerful Features</h2>
        <v-row>
          <v-col v-for="(feature, index) in features" :key="index" cols="12" md="4">
            <v-card elevation="2" hover style="border-radius: 12px" class="pa-6 h-100">
              <v-icon color="#2ab3b8" size="40" class="mb-4">{{ feature.icon }}</v-icon>
              <h3 class="text-h5 mb-4">{{ feature.title }}</h3>
              <p class="text-subtitle-1">{{ feature.description }}</p>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Secondary CTA -->
    <v-row class="mb-12">
      <v-col cols="12" sm="8" offset-sm="2">
        <v-card
          elevation="4"
          class="text-center pa-12"
          style="border-radius: 16px; background: linear-gradient(135deg, #2ab3b8, #1e9599)"
        >
          <h2 class="text-h4 white--text mb-6">Ready to Automate Your Workflows?</h2>
          <v-btn x-large class="white text--primary" elevation="2" to="/login">
            Get Started
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import APIService from "@/services/APIService";
import heroImage from "@/assets/objective_complete.jpg";

export default {
  name: "ProcessAutomationLanding",
  components: {},
  data() {
    return {
      name: "",
      email: "",
      submitted: false,
      hero_url: heroImage,
      quickLinks: [
        { text: "Benefits", target: "benefits" },
        { text: "Features", target: "features" },
        { text: "Use Cases", target: "use-cases" },
      ],
      benefits: [
        {
          title: "Visual Simplicity",
          description: "No complex diagrams - just intuitive Kanban boards",
          icon: "mdi-view-column",
        },
        {
          title: "Automated Actions",
          description: "Trigger actions automatically as work moves through stages",
          icon: "mdi-robot",
        },
        {
          title: "Flexible Configuration",
          description: "Define your own fields, stages, and automation rules",
          icon: "mdi-cog",
        },
        {
          title: "Near Real-time Updates",
          description: "See changes across your team within seconds",
          icon: "mdi-flash",
        },
      ],
      features: [
        {
          title: "Kanban Boards",
          description: "Visual workflow management that everyone understands",
          icon: "mdi-view-dashboard",
        },
        {
          title: "Custom Fields",
          description: "Define exactly the data you need to track",
          icon: "mdi-form-textbox",
        },
        {
          title: "Workflow Rules",
          description: "Automate emails, notifications, and updates",
          icon: "mdi-transit-connection-variant",
        },
        {
          title: "Team Collaboration",
          description: "Multi-user access with role-based permissions",
          icon: "mdi-account-group",
        },
        {
          title: "Integration Ready",
          description: "Connect to your existing systems via API",
          icon: "mdi-connection",
        },
        {
          title: "Secrets Management",
          description: "Securely store API keys and credentials for use in automation",
          icon: "mdi-key-variant",
        },
      ],
      metrics: [
        {
          stat: "Minutes",
          description: "To set up your first automated board",
          icon: "mdi-rocket-launch",
        },
        {
          stat: "Secure",
          description: "Role-based access, isolated tenant data, and encrypted secrets",
          icon: "mdi-shield-check",
        },
        {
          stat: "Low-code",
          description: "No programming required for boards; simple syntax for automation",
          icon: "mdi-code-tags",
        },
      ],
      useCases: [
        {
          title: "Job Applications",
          description: "Track candidates from application to offer",
        },
        {
          title: "Sales Pipeline",
          description: "Manage leads through your sales process",
        },
        {
          title: "Project Management",
          description: "Move tasks through planning, development, and delivery",
        },
        {
          title: "Customer Support",
          description: "Handle tickets from submission to resolution",
        },
        {
          title: "Content Production",
          description: "Manage content from ideation to publication",
        },
        {
          title: "Procurement",
          description: "Track purchase requests through approval and fulfillment",
        },
      ],
    };
  },
  computed: {
    isFormValid() {
      return this.name && this.email;
    },
  },
  methods: {
    handleSubmit() {
      if (this.isFormValid) {
        const to_send = `Name: ${this.name}\nEmail: ${this.email}\n`;
        APIService.sns(`Process Automation Beta Signup: ${this.name}`, to_send).then((response) => {
          console.log(response.data);
        });
        this.submitted = true;
      }
    },
    scrollTo(target) {
      const element = document.getElementById(target);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      } else if (target === "top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
  },
};
</script>

<style scoped>
.landing-container {
  background: linear-gradient(to bottom, #ffffff, #f5f5f5);
  min-height: 100vh;
  padding-top: 2rem;
}

.hero-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 24px;
}

.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 25px rgba(42, 179, 184, 0.1);
}

@media (max-width: 1264px) {
  .landing-container {
    padding-top: 1rem;
  }

  .hero-section {
    padding: 2rem 0;
  }
}
</style>
